import React, { useEffect, useState } from "react";
import MagicTable from "../common/MagicTable";
import Box from "../mui/Box";
import TableHeader from "../common/MagicPageHeader";
import Header from "../common/MagicTableHeader";
import ArrowUpwardIcon from "@mui/icons-material/ArrowUpward";
import ArrowDownwardIcon from "@mui/icons-material/ArrowDownward";
import DownloadToXlsxFile from "../../helper/DownloadToXlsxFile";
import ImageComponent from "../mui/Image";
import { useFetchScoreWiseReportsQuery } from "../../store/apis/reportsApis";

interface TabCategory {
  id: number;
  name: string;
  code:string;
}

interface ScoreWiseTableProps {
  selectedTab: TabCategory | null;
  magicPageHeaderBtn: {
    text: string;
    variant: string;
    startIcon: React.ReactNode;
    onClick: () => void;
    sx: Record<string, string>;
  }[];
  magicTableHeaderBtn: any[];
  params:any;
  isInvalidSearch: boolean;
  onPaginationChange: (pageNo: number) => void;
  onSearch: (searchTerm: string) => void;
}

const ScoreWiseTable: React.FC<ScoreWiseTableProps> = ({
  selectedTab,
  magicTableHeaderBtn,
  params,
  isInvalidSearch,
  onPaginationChange,
  onSearch,
}) => {
  const [downloadParams, setDownloadParams] = useState<any>(null);
  const [downloadTrigger, setDownloadTrigger] = useState(false);
  const filteredParams = { ...params };
  delete filteredParams.quarterMasterId;
  const columns = [
    {
      field: "centre",
      headerName: "Centre",
      flex: 1,
      minWidth: 300,
      sortable: false,
      renderCell: (params: any) => params.row.centre || "-",
    },
    {
      field: "quarter1",
      headerName: "Quarter 1",
      flex: 1,
      minWidth: 100,
      sortable: false,
      renderCell: (params: any) => params.row.quarter1 || "-",
    },
    {
      field: "quarter2",
      headerName: "Quarter 2",
      flex: 1,
      minWidth: 100,
      sortable: false,
      renderCell: (params: any) => {
        const score = params.row.quarter2 || "-";
        const isIncrease = params.row.quarter2Trend === "up";
        return (
          <Box sx={{ display: "flex", alignItems: "center" }}>
            {score}
            {isIncrease ? (
              <ArrowUpwardIcon sx={{ color: "green", marginLeft: "5px" }} />
            ) : (
              <ArrowDownwardIcon sx={{ color: "red", marginLeft: "5px" }} />
            )}
          </Box>
        );
      },
    },
    {
      field: "quarter3",
      headerName: "Quarter 3",
      flex: 1,
      minWidth: 100,
      sortable: false,
      renderCell: (params: any) => {
        const score = params.row.quarter3 || "-";
        const isIncrease = params.row.quarter3Trend === "up";
        return (
          <Box sx={{ display: "flex", alignItems: "center" }}>
            {score}
            {isIncrease ? (
              <ArrowUpwardIcon sx={{ color: "green", marginLeft: "5px" }} />
            ) : (
              <ArrowDownwardIcon sx={{ color: "red", marginLeft: "5px" }} />
            )}
          </Box>
        );
      },
    },
    {
      field: "quarter4",
      headerName: "Quarter 4",
      flex: 1,
      minWidth: 100,
      sortable: false,
      renderCell: (params: any) => {
        const score = params.row.quarter4 || "-";
        const isIncrease = params.row.quarter4Trend === "up";
        return (
          <Box sx={{ display: "flex", alignItems: "center" }}>
            {score}
            {isIncrease ? (
              <ArrowUpwardIcon sx={{ color: "green", marginLeft: "5px" }} />
            ) : (
              <ArrowDownwardIcon sx={{ color: "red", marginLeft: "5px" }} />
            )}
          </Box>
        );
      },
    },
  ];
  const {
    data: fetchedReports,isLoading
  } = useFetchScoreWiseReportsQuery(filteredParams);
  const { data: downloadData,isLoading: downloadLoading  } = useFetchScoreWiseReportsQuery(downloadParams, {
    skip: !downloadParams,
  });
  const totalPages = Math.max(
    1,
    Math.ceil((fetchedReports?.reports?.length ?? 0) / 10)
  );
  const handleDownload = async () => {
    try {
      const downloadPayload = {
        ...filteredParams,
        pageNo: "1",
        pageSize: fetchedReports?.totalReports || "10",
      };
      
      setDownloadParams(downloadPayload);
      setDownloadTrigger(true); 
    } catch (error) {
      console.error("Error during download:", error);
    }
  };
  useEffect(() => {
    if (downloadData && downloadTrigger) {

  const sheetData = [
    ["Centre", "Quarter 1", "Quarter 2", "Trend (Q2)", "Quarter 3", "Trend (Q3)", "Quarter 4", "Trend (Q4)"],
    ...downloadData?.reports.map((report: any) => [
      report.centre || "-",
      report.quarter1 || "-",
      report.quarter2 || "-",
      report.quarter2Trend === "up" ? "⬆" : report.quarter2Trend === "down" ? "⬇" : "-",
      report.quarter3 || "-",
      report.quarter3Trend === "up" ? "⬆" : report.quarter3Trend === "down" ? "⬇" : "-",
      report.quarter4 || "-",
      report.quarter4Trend === "up" ? "⬆" : report.quarter4Trend === "down" ? "⬇" : "-",
    ]),
  ];

  DownloadToXlsxFile({
    fileNamePrifix: "score-wise-report",
    sheetData,
  });  setDownloadTrigger(false);  
}
}, [downloadData, downloadTrigger]);  

  const magicPageHeaderBtn = [
    {
      text: "Download",
      variant: "outlined",
      startIcon: (
        <ImageComponent src="/assets/images/login/exportIcon.svg" alt="ExportIcon" />
      ),
      onClick: handleDownload,
      loading: downloadLoading,
      sx: {
        background: "white",
        border: "1px solid #D0D5DD",
        borderRadius: "8px",
        color: "black",
      },
    },
  ];
  return (
    <>
      <TableHeader
        title={`${selectedTab?.name || "Score wise"} Report`}
        subTitle="Here you can view the report with all the details."
        buttons={magicPageHeaderBtn}
        chipLabel={`${fetchedReports?.reports?.length || 0} entries`}
      />
      <Header
        buttons={magicTableHeaderBtn}
        onSearchfieldChange={(value) => onSearch(String(value))}
        isInvalidSearch={isInvalidSearch}
      />
      <MagicTable
        columns={columns}
        rows={fetchedReports?.reports}
        loading={isLoading}
        handlePageChange={(event, pageNo) => onPaginationChange(pageNo)}
        currentPage={parseInt(params?.pageNo) || 1}
        count={totalPages}
        rowCellsSx={{ paddingLeft: "16px", textAlign: "start" }}
        headerSx={{
          paddingLeft: "16px",
          textAlign: "start",
          fontWeight: 600,
          color: "#242424",
          padding: "13px 16px",
        }}
        tableSx={{ minHeight: "300px", width: "100%" }}
        sx={{
          "& .MuiDataGrid-columnHeader": {
            backgroundColor: "#DADADA",
          },
          "& .MuiDataGrid-columnHeaderTitle": {
            fontWeight: 550,
            fontSize: "14px",
          },
          "& .MuiDataGrid-row": {
            backgroundColor: "#FFFFFF",
          },
          "& .MuiDataGrid-row:hover": {
            backgroundColor: "#FFFFFF",
          },
          "& .MuiDataGrid-cell": {
            display: "flex",
            alignItems: "center",
          },}}
      />
    </>
  );
};

export default ScoreWiseTable;
